import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Phone,
  CreditCard,
  Calendar,
  UserCheck,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Banknote,
  CalendarDays
} from 'lucide-react';
import { formatDate } from '../../utils/date';

const PaymentCard = ({ traveler, campaignName, onEdit, onDelete, onViewDetails }) => {
  const { t } = useTranslation();

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'pending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return CheckCircle;
      case 'partial':
        return Clock;
      case 'pending':
        return Calendar;
      case 'overdue':
        return AlertCircle;
      default:
        return XCircle;
    }
  };

  const getPaymentTypeIcon = (type) => {
    return type === 'cash' ? Banknote : CalendarDays;
  };

  const getPaymentTypeColor = (type) => {
    return type === 'cash'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  };

  const calculateProgress = () => {
    if (traveler.paymentType === 'cash') {
      return traveler.paymentStatus === 'paid' ? 100 : 0;
    } else {
      const totalAmount = traveler.totalAmount || 0;
      const paidAmount = totalAmount - (traveler.remainingBalance || 0);
      return totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
    }
  };

  const StatusIcon = getStatusIcon(traveler.paymentStatus);
  const PaymentTypeIcon = getPaymentTypeIcon(traveler.paymentType);
  const progress = calculateProgress();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <User className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              {traveler.fullName}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {campaignName}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(traveler.paymentStatus)}`}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {t(`payments.${traveler.paymentStatus}`)}
          </span>
        </div>
      </div>

      {/* Contact Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Phone className="h-4 w-4 mr-2" />
          <span>{traveler.primaryPhone}</span>
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <CreditCard className="h-4 w-4 mr-2" />
          <span>{traveler.passportNumber}</span>
        </div>
        {traveler.agentName && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <UserCheck className="h-4 w-4 mr-2" />
            <span>{traveler.agentName}</span>
          </div>
        )}
      </div>

      {/* Payment Information */}
      <div className="space-y-3 mb-4">
        {/* Payment Type */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('payments.paymentType')}
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentTypeColor(traveler.paymentType)}`}>
            <PaymentTypeIcon className="h-3 w-3 mr-1" />
            {t(`payments.${traveler.paymentType}`)}
          </span>
        </div>

        {/* Total Amount */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('payments.totalAmount')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {(traveler.totalAmount || 0).toLocaleString()} {t('common:currency')}
          </span>
        </div>

        {/* Remaining Balance */}
        {traveler.paymentType === 'installments' && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('payments.remainingBalance')}
            </span>
            <span className={`font-semibold ${
              (traveler.remainingBalance || 0) > 0 
                ? 'text-red-600 dark:text-red-400' 
                : 'text-green-600 dark:text-green-400'
            }`}>
              {(traveler.remainingBalance || 0).toLocaleString()} {t('common:currency')}
            </span>
          </div>
        )}

        {/* Payment Progress */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('payments.paymentProgress')}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {progress.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                progress === 100 
                  ? 'bg-green-500' 
                  : progress > 50 
                    ? 'bg-blue-500' 
                    : 'bg-yellow-500'
              }`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Installment Details */}
      {traveler.paymentType === 'installments' && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            {t('payments.installmentDetails')}
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                {t('payments.downPayment')}
              </span>
              <p className="font-medium text-gray-900 dark:text-white">
                {(traveler.downPayment || 0).toLocaleString()} {t('common:currency')}
              </p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                {t('payments.monthlyInstallment')}
              </span>
              <p className="font-medium text-gray-900 dark:text-white">
                {(traveler.monthlyInstallment || 0).toLocaleString()} {t('common:currency')}
              </p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                {t('payments.paymentDuration')}
              </span>
              <p className="font-medium text-gray-900 dark:text-white">
                {traveler.paymentDuration || 0} {t('payments.months')}
              </p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                {t('payments.nextPaymentDue')}
              </span>
              <p className="font-medium text-gray-900 dark:text-white">
                {formatDate(traveler.nextPaymentDate)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Agent Information */}
      {traveler.agentName && traveler.agentCommission && (
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 mb-4">
          <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
            {t('payments.agentInfo')}
          </h4>
          <div className="flex items-center justify-between text-xs">
            <span className="text-purple-700 dark:text-purple-300">
              {t('payments.agentCommission')}
            </span>
            <span className="font-medium text-purple-900 dark:text-purple-100">
              {(traveler.agentCommission || 0).toLocaleString()} {t('common:currency')}
            </span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
        <button
          onClick={onViewDetails}
          className="flex items-center px-3 py-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium transition-colors"
        >
          <Eye className="h-4 w-4 mr-1" />
          {t('common.view')}
        </button>
        <div className="flex items-center space-x-2">
          <button
            onClick={onEdit}
            className="flex items-center px-3 py-1.5 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium transition-colors"
          >
            <Edit className="h-4 w-4 mr-1" />
            {t('common.edit')}
          </button>
          <button
            onClick={onDelete}
            className="flex items-center px-3 py-1.5 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium transition-colors"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentCard;